import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type {
  DataResponse,
  TableResponse,
  PaginatedResponse,
  TableDataResponse,
} from 'src/types/common';

import type {
  IEventLog,
  IBaseInvoice,
  ITransaction,
  IInvoiceHistory,
  IInvoiceViewRow,
  IInvoiceToUpdate,
  IModifyAndResend,
  IInvoiceStatistics,
  ICreditInvoiceRequest,
  ISingleInvoiceResponse,
  DirectDebitRetryAttempts,
  IInvoiceModifyEligibility,
  IInvoiceResetStageRequest,
  IInvoiceResetStagePreview,
  InvoiceDocumentPreviewType,
} from 'src/types/invoices';
import {
  EventLogType,
  HistoriesType,
  InvoiceStatus,
  InvoicePaymentStatus,
} from 'src/types/invoices';

import type {
  IInvoicePause,
  ICancelInvoicePause,
  ICreateInvoicePause,
  IUpdateInvoicePause,
} from 'src/types/invoicePauses';

import { DIRECT_DEBIT_FAILED_REASONS } from 'src/types/eventLog';
import dayjs from 'dayjs';
import type { IPaginationState } from 'src/utils/server-side-pagination';
import { convertObjToUrl } from 'src/utils/server-side-pagination';
import { parseBaseInvoiceFromFlatInvoiceRow } from 'src/utils/payload-parsers/invoiceParsers';
import type { SUPPORTED_TABLES_ENUM } from 'src/bop-view-definitions';

export const invoicesAPISlice = createApi({
  reducerPath: 'presentation-service-invoices-api',
  tagTypes: ['Invoices', 'EventLogs', 'Customers', 'InvoicePauses'],
  baseQuery: authQuery,
  endpoints: (builder) => ({
    // getInvoices: builder.query<TableResponse<IBaseInvoice>, void>({
    //   query: () => '/invoices?page=1&limit=5000&sort=createdAt&order=desc',
    //   transformResponse: (res: TableResponse<IBaseInvoice>) => {
    //     res.rows = res.rows.map((x: any) => parseBaseInvoice(x));

    //     return res;
    //   },
    //   providesTags: ['Invoices'],
    // }),

    //
    // Paginated invoices, with the visible columns resolved from a BOP view
    // definition. `tableName` selects which definition: omit it for the
    // invoices list (the plain `/invoices` route), or pass one for a grid that
    // is configured separately, e.g. the customer profile's invoice list.
    //
    getInvoices: builder.query<
      PaginatedResponse<IBaseInvoice>,
      { state: IPaginationState; limit: number; tableName?: SUPPORTED_TABLES_ENUM }
    >({
      query: ({ state, limit, tableName }) =>
        `/invoices${tableName ? `/view/${tableName}` : ''}?limit=${limit}${convertObjToUrl(state)}`,
      transformResponse: (res: PaginatedResponse<IInvoiceViewRow>) => ({
        ...res,
        data: (res.data ?? []).map(parseBaseInvoiceFromFlatInvoiceRow),
      }),
      providesTags: ['Invoices'],
    }),

    //
    // Get invoices for certain customer - this API should be view-definition independent
    // fetching pre-defined columns which do not rely on what exists as view-definition record
    //
    getInvoicesPerCustomer: builder.query<
      PaginatedResponse<IBaseInvoice>,
      { state: IPaginationState; limit: number; customerId: string }
    >({
      query: ({ state, limit, customerId }) =>
        `/invoices/per-customer/${customerId}?limit=${limit}${convertObjToUrl(state)}`,
      transformResponse: (res: PaginatedResponse<IInvoiceViewRow>) => ({
        ...res,
        data: (res.data ?? []).map(parseBaseInvoiceFromFlatInvoiceRow),
      }),
      providesTags: ['Invoices'],
    }),

    // Get invoices for a certain client - this API should be view-definition independent
    getInvoicesPerClient: builder.query<
      PaginatedResponse<IBaseInvoice>,
      { state: IPaginationState; limit: number; clientId: string }
    >({
      query: ({ state, limit, clientId }) =>
        `/invoices/per-client/${clientId}?limit=${limit}${convertObjToUrl(state)}`,
      transformResponse: (res: PaginatedResponse<IInvoiceViewRow>) => ({
        ...res,
        data: (res.data ?? []).map(parseBaseInvoiceFromFlatInvoiceRow),
      }),
      providesTags: ['Invoices'],
    }),

    //
    // `tableName` tells the backend which BOP view definition's columns to
    // export, so a grid exports the columns it is actually showing.
    //
    exportInvoices: builder.mutation<
      { data: { id: string } },
      { state: IPaginationState; tableName: SUPPORTED_TABLES_ENUM }
    >({
      query: ({ state, tableName }) => ({
        // limit will be ignored, but I've included it for the sake of parity
        url: `/invoices/generate-export?limit=10${convertObjToUrl(state)}`,
        method: 'POST',
        body: { tableName },
      }),
    }),

    getInvoicesByClientId: builder.query<PaginatedResponse<IBaseInvoice>, string>({
      query: (clientId) =>
        `/invoices?page=1&limit=5000&sort=createdAt&order=desc&clientId=${clientId}`,
      transformResponse: (res: PaginatedResponse<IBaseInvoice>) => {
        res.data = res.data.map((x: any) => parseBaseInvoice(x));

        return res;
      },
      providesTags: ['Invoices'],
    }),

    getInvoiceById: builder.query<ISingleInvoiceResponse, { id: string; customerId: string }>({
      query: ({ id, customerId }) => `/invoices/${id}?customerId=${customerId}`,
      transformResponse: (res: any) => {
        res = {
          ...res,
          status: res.status,
          stage: res.stage,
          paymentStatus: res.paymentStatus,
          totalRemainingBalance: parseFloat(res.totalRemainingBalance),
          totalAmount: parseFloat(res.totalAmount),
          payments: res.payment_read_view,
        };

        return res;
      },
      providesTags: ['Invoices'],
    }),

    checkInvoiceStage: builder.query<ISingleInvoiceResponse, { id: string; customerId: string }>({
      query: ({ id, customerId }) => `/invoices/${id}?customerId=${customerId}`,
      transformResponse: (res: any) => {
        res = {
          ...res,
          stage: res.stage,
        };

        return res;
      },
    }),

    getInvoiceStatistics: builder.query<IInvoiceStatistics, string | void>({
      query: (customerId) => `/invoices/statistics${customerId ? `?customerId=${customerId}` : ''}`,
    }),

    searchInvoices: builder.query<TableResponse<IBaseInvoice>, string>({
      query: (param) => '/invoices/search' + param + '&excludeCredit=true',
      transformResponse: (res: TableResponse<IBaseInvoice>) => {
        res.rows = res.rows.map((x: any) => parseBaseInvoice(x));

        return res;
      },
    }),

    getEventLogs: builder.query<IEventLog[], string>({
      query: (id) => `/invoices/${id}/event-log`,
      providesTags: ['EventLogs'],
      transformResponse: (res: IEventLog[]) => {
        // res.rows = res.map(el => )
        const modifiedRes = (
          res as (IEventLog & {
            isThereFallback: boolean;
          })[]
        ).map((el, i, arr) => {
          // if (el.distributionChannel === EventLogDistributionChannel.EDI) {
          //   if (arr.some((e) => e.updateReason === 'edi_fallback')) {
          //     el.isThereFallback = true;
          //   }
          // }

          // if (el.distributionChannel === EventLogDistributionChannel.Einvoice) {
          //   if (arr.some((e) => e.updateReason === 'einv_fallback')) {
          //     el.isThereFallback = true;
          //   }
          // }

          if (DIRECT_DEBIT_FAILED_REASONS.includes(el.updateReason as any)) {
            el.isThereFallback = true;
            el.isDirectDebitFailed = true;
          }

          return el;
        });

        return modifiedRes;
      },
    }),

    // -------------------------------------------------PAYMENT ACTIONS START------------------------------------------
    setCretidLoss: builder.query<
      TableDataResponse<ITransaction>,
      {
        id: string;
        customerId: string;
        invoices: IInvoiceToUpdate[];
      }
    >({
      query: ({ invoices }) => ({
        url: `/payments/create`,
        method: 'POST',
        body: invoices,
      }),
      async onQueryStarted({ id, customerId }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          dispatch(
            invoicesAPISlice.util.updateQueryData(
              'getInvoiceById',
              { id, customerId },
              (invoiceDraft) => {
                const newEvent: IInvoiceHistory = {
                  id: '',
                  invoiceId: id,
                  invoiceNo: invoiceDraft.invoiceNo,
                  amount: invoiceDraft.totalRemainingBalance,
                  type: HistoriesType.PaymentConfirmed,
                  date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                  messageData: {
                    type: HistoriesType.PaymentConfirmed,
                    pdfUrl: `${window.RUNTIME_CONFIG.FILE_SERVICE}/invoice-${id}.pdf`,
                    dueDate: invoiceDraft.dueDate,
                    subject: 'Payment Confirmation',
                    distributionChannel: 'email',
                    remainingAmount: '0',
                    snapshotStatus: InvoiceStatus.Closed,
                  },
                  currency: invoiceDraft.currency || '',
                };

                invoiceDraft.histories.push(newEvent);

                const newTransaction = queryResponse.data.data[0];

                invoiceDraft.payments.push(newTransaction);
                invoiceDraft.status = InvoiceStatus.Closed;
                invoiceDraft.paymentStatus = InvoicePaymentStatus.Paid;
              }
            )
          );
        } catch {
          console.log('Error updating invoice!');
        }
      },
    }),

    goodWillWaive: builder.query<
      TableDataResponse<ITransaction>,
      { id: string; customerId: string; invoices: IInvoiceToUpdate[] }
    >({
      query: ({ invoices }) => ({
        url: `/payments/create`,
        method: 'POST',
        body: invoices,
      }),
      async onQueryStarted({ id, customerId, invoices }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;
          const amountPaid = invoices[0].amount;

          dispatch(
            invoicesAPISlice.util.updateQueryData(
              'getInvoiceById',
              { id, customerId },
              (invoiceDraft) => {
                const isFullyPaid = amountPaid == invoiceDraft.totalRemainingBalance;

                // const newEvent: IInvoiceHistory = {
                //   id: '',
                //   invoiceId: id,
                //   invoiceNo: invoiceDraft.invoiceNo,
                //   amount: null,
                //   type: HistoriesType.GoodWill,
                //   date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                //   messageData: {
                //     to: '',
                //     from: '',
                //     type: 'email',
                //     pdfUrl: `${FILE_SERVICE_URL}/invoice-${id}.pdf`,
                //     dueDate: invoiceDraft.dueDate,
                //     subject: '',
                //     distributionChannel: 'email',
                //   },
                //   currency: invoiceDraft.serviceLine.currency,
                // };

                // invoiceDraft.histories.push(newEvent);

                const newTransaction = queryResponse.data.data[0];

                invoiceDraft.payments.push(newTransaction);
                invoiceDraft.status = isFullyPaid ? InvoiceStatus.Closed : InvoiceStatus.Open;
                invoiceDraft.paymentStatus = isFullyPaid
                  ? InvoicePaymentStatus.Paid
                  : InvoicePaymentStatus.PartiallyPaid;

                if (typeof amountPaid === 'number') {
                  invoiceDraft.remainingAmount = invoiceDraft.remainingAmount - amountPaid!;
                  invoiceDraft.totalAmount = invoiceDraft.totalAmount - amountPaid!;
                } else if (typeof amountPaid === 'object' && amountPaid !== null) {
                  invoiceDraft.remainingAmount =
                    invoiceDraft.remainingAmount -
                    (amountPaid.capital || 0) -
                    (amountPaid.fee || 0);
                  invoiceDraft.totalAmount =
                    invoiceDraft.totalAmount - (amountPaid.capital || 0) - (amountPaid.fee || 0);
                }
              }
            )
          );
        } catch {
          console.log('Error updating invoice!');
        }
      },
    }),

    directPayment: builder.query<
      TableDataResponse<ITransaction>,
      { id: string; customerId: string; invoices: IInvoiceToUpdate[] }
    >({
      query: ({ invoices }) => ({
        url: `/payments/create`,
        method: 'POST',
        body: invoices,
      }),
      async onQueryStarted({ id, customerId, invoices }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;
          const amountPaid = invoices[0].amount || 0;

          dispatch(
            invoicesAPISlice.util.updateQueryData(
              'getInvoiceById',
              { id, customerId },
              (invoiceDraft) => {
                const isFullyPaid = amountPaid == invoiceDraft.totalRemainingBalance;

                let calculatedAmount = 0;

                if (typeof invoices[0].amount === 'number') {
                  calculatedAmount = invoices[0].amount;
                } else if (typeof invoices[0].amount === 'object' && invoices[0].amount !== null) {
                  calculatedAmount =
                    (invoices[0].amount.capital || 0) + (invoices[0].amount.fee || 0);
                }

                const newEvent: IInvoiceHistory = {
                  id: '',
                  invoiceId: id,
                  invoiceNo: invoiceDraft.invoiceNo,
                  amount: calculatedAmount,
                  type: HistoriesType.PaymentConfirmed,
                  date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                  messageData: {
                    type: 'payment',
                    to: invoiceDraft.mobilePhoneNo,
                    pdfUrl: `${window.RUNTIME_CONFIG.FILE_SERVICE}/invoice-${id}.pdf`,
                    dueDate: invoiceDraft.dueDate,
                    subject: 'Partial Payment Confirmation',
                    distributionChannel: 'sms',
                    remainingAmount: (
                      invoiceDraft.totalRemainingBalance - calculatedAmount
                    ).toString(),
                    snapshotStatus: invoiceDraft.status as InvoiceStatus,
                  },
                  currency: invoiceDraft.currency || '',
                };

                invoiceDraft.histories.push(newEvent);
                const newTransaction = queryResponse.data.data[0];

                invoiceDraft.payments.push(newTransaction);
                invoiceDraft.status = isFullyPaid ? InvoiceStatus.Closed : InvoiceStatus.Open;
                invoiceDraft.paymentStatus = isFullyPaid
                  ? InvoicePaymentStatus.Paid
                  : InvoicePaymentStatus.PartiallyPaid;
                if (typeof amountPaid === 'number') {
                  invoiceDraft.remainingAmount = invoiceDraft.remainingAmount - amountPaid;
                  invoiceDraft.totalAmount = invoiceDraft.totalAmount - amountPaid;
                } else if (typeof amountPaid === 'object' && amountPaid !== null) {
                  invoiceDraft.remainingAmount =
                    invoiceDraft.remainingAmount -
                    (amountPaid.capital || 0) -
                    (amountPaid.fee || 0);
                  invoiceDraft.totalAmount =
                    invoiceDraft.totalAmount - (amountPaid.capital || 0) - (amountPaid.fee || 0);
                }
              }
            )
          );
        } catch {
          console.log('Error updating invoice!');
        }
      },
    }),

    cancelInvoices: builder.query<
      TableDataResponse<ITransaction>,
      { id: string; customerId: string; invoices: IInvoiceToUpdate[] }
    >({
      query: ({ invoices }) => ({
        url: `/payments/create`,
        method: 'POST',
        body: invoices,
      }),
      async onQueryStarted({ id, customerId, invoices }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;
          const amountPaid = invoices[0].amount;

          dispatch(
            invoicesAPISlice.util.updateQueryData(
              'getInvoiceById',
              { id, customerId },
              (invoiceDraft) => {
                const newEvent: IInvoiceHistory = {
                  id: '',
                  invoiceId: id,
                  invoiceNo: invoiceDraft.invoiceNo,
                  amount: invoiceDraft.totalRemainingBalance,
                  type: HistoriesType.PaymentConfirmed,
                  date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                  messageData: {
                    type: HistoriesType.PaymentConfirmed,
                    pdfUrl: `${window.RUNTIME_CONFIG.FILE_SERVICE}/invoice-${id}.pdf`,
                    dueDate: invoiceDraft.dueDate,
                    subject: 'Payment Confirmation',
                    distributionChannel: 'email',
                    remainingAmount: '0',
                    snapshotStatus: InvoiceStatus.Cancelled,
                  },
                  currency: invoiceDraft.currency || '',
                };

                invoiceDraft.histories.push(newEvent);

                const newTransaction = queryResponse.data.data[0];

                invoiceDraft.payments.push(newTransaction);
                invoiceDraft.status = InvoiceStatus.Cancelled;
                invoiceDraft.paymentStatus = InvoicePaymentStatus.Paid;
              }
            )
          );
        } catch {
          console.log('Error updating invoice!');
        }
      },
    }),

    // -------------------------------------------------PAYMENT ACTIONS END------------------------------------------

    postponeInvoice: builder.query<
      IBaseInvoice,
      { id: string; customerId: string; postponeDate: string; updateReason: string }
    >({
      query: ({ id, postponeDate, updateReason }) => ({
        url: `/invoices/update-due-date/${id}`,
        method: 'POST',
        body: { dueDate: postponeDate, updateReason },
      }),
      async onQueryStarted({ id, customerId, postponeDate }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          dispatch(
            invoicesAPISlice.util.updateQueryData(
              'getInvoiceById',
              { id, customerId },
              (invoiceDraft) => {
                const newEvent: IInvoiceHistory = {
                  id: '',
                  invoiceId: id,
                  invoiceNo: invoiceDraft.invoiceNo,
                  amount: null,
                  type: HistoriesType.DueDateUpdated,
                  date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                  messageData: {
                    newDueDate: postponeDate,
                    oldDueDate: invoiceDraft.dueDate,
                  },
                };

                invoiceDraft.histories.push(newEvent);
              }
            )
          );
        } catch {
          console.log('Error updating invoice!');
        }
      },
    }),

    adjustDueDate: builder.query<
      ISingleInvoiceResponse,
      {
        id: string;
        customerId: string;
        dueDate: string;
        updateReason: string;
        feeHandling: 'reset' | 'keep';
      }
    >({
      query: ({ id, dueDate, updateReason, feeHandling }) => ({
        url: `/invoices/adjust-due-date/${id}`,
        method: 'POST',
        body: { dueDate, updateReason, feeHandling },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices']));
        } catch {
          console.log('Error adjusting due date!');
        }
      },
    }),

    invoiceStopUntil: builder.query<
      IBaseInvoice,
      {
        id: string;
        customerId: string;
        reminderDate: string | null;
        dcStopDate: string | null;
        reminderReason: string;
        status: InvoiceStatus;
      }
    >({
      query: ({ id, reminderDate, dcStopDate, reminderReason }) => ({
        url: `/invoices/update-stop-until-date/${id}`,
        method: 'POST',
        body: {
          metadata: {
            invoicing4StopUntilDate: dcStopDate,
            invoicing2StopUntilDate: reminderDate,
          },
          escalationStopReason: reminderReason,
        },
      }),
      async onQueryStarted(
        { id, reminderDate, dcStopDate, reminderReason, customerId, status },
        { dispatch, queryFulfilled }
      ) {
        try {
          const queryResponse = await queryFulfilled;

          dispatch(
            invoicesAPISlice.util.updateQueryData(
              'getInvoiceById',
              { id, customerId },
              (invoiceDraft) => {
                const newEvent: IInvoiceHistory = {
                  id: '',
                  invoiceId: id,
                  invoiceNo: invoiceDraft.invoiceNo,
                  amount: null,
                  type: HistoriesType.StopUntilDateUpdated,
                  date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                  messageData: {
                    metadata: {
                      invoicing2StopUntilDate: reminderDate,
                      invoicing4StopUntilDate: dcStopDate,
                    },
                    escalationStopReason: reminderReason,
                  },
                };

                invoiceDraft.histories.push(newEvent);
              }
            )
          );

          dispatch(
            invoicesAPISlice.util.updateQueryData('getEventLogs', id, (eventLogsDraft) => {
              const newEvent: IEventLog = {
                id: '',
                invoiceId: id,
                type: EventLogType.DateChange,
                eventDate: new Date().toDateString(),
                eventName: 'DCStopUntilDateChanged',
                triggeredBy: 'first last',
                updatedDate: dcStopDate,
                statusSnapshot: status,
                updateReason: reminderReason,
              };

              eventLogsDraft.push(newEvent);
            })
          );
        } catch {
          console.log('Error updating invoice!');
        }
      },
    }),

    creditInvoice: builder.query<
      IBaseInvoice,
      { id: string; customerId: string; invoice: ICreditInvoiceRequest }
    >({
      query: ({ id, invoice }) => ({
        url: `/invoices/credit-invoice/${id}`,
        method: 'POST',
        body: invoice,
      }),
      async onQueryStarted({ id, customerId }, { dispatch, queryFulfilled }) {
        // try {
        //   const queryResponse = await queryFulfilled;
        //   const amountPaid = invoices[0].amount;

        //   dispatch(
        //     invoicesAPISlice.util.updateQueryData(
        //       'getInvoiceById',
        //       { id, customerId },
        //       (invoiceDraft) => {
        //         const newEvent: IInvoiceHistory = {
        //           id: '',
        //           invoiceId: id,
        //           invoiceNo: invoiceDraft.invoiceNo,
        //           amount: invoiceDraft.totalRemainingBalance,
        //           type: HistoriesType.PaymentConfirmed,
        //           date: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        //           messageData: {
        //             type: HistoriesType.PaymentConfirmed,
        //             pdfUrl: `${FILE_SERVICE_URL}/invoice-${id}.pdf`,
        //             dueDate: invoiceDraft.dueDate,
        //             subject: 'Payment Confirmation',
        //             distributionChannel: 'email',
        //             remainingAmount: '0',
        //             snapshotStatus: InvoiceStatus.Cancelled,
        //           },
        //           currency: invoiceDraft.serviceLine.currency,
        //         };

        //         invoiceDraft.histories.push(newEvent);

        //         const newTransaction = queryResponse.data.data[0];

        //         invoiceDraft.payments.push(newTransaction);
        //         invoiceDraft.status = InvoiceStatus.Cancelled;
        //         invoiceDraft.paymentStatus = InvoicePaymentStatus.Paid;
        //       }
        //     )
        //   );
        // } catch {
        //   console.log('Error updating invoice!');
        // }

        // TODO: Fix
        try {
          const queryResponse = await queryFulfilled;

          await delay(10000);

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));

          return;
        } catch {
          // Handle errors
        }
      },
    }),

    newCreditInvoice: builder.query<
      IBaseInvoice,
      { customerId: string; invoice: ICreditInvoiceRequest }
    >({
      query: ({ customerId, invoice }) => ({
        url: `/invoices/customers/${customerId}/credit-invoice`,
        method: 'POST',
        body: invoice,
      }),
      async onQueryStarted({ invoice }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          await delay(10000);

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));

          return;
        } catch {
          // Handle errors
        }
      },
    }),

    matchCreditInvoice: builder.query<
      IBaseInvoice,
      {
        invoiceId: string;
        creditInvoiceNo: string;
        amount: number;
        paidDate?: string;
        additionalInformation?: string;
      }
    >({
      query: ({ invoiceId, creditInvoiceNo, amount, paidDate, additionalInformation }) => ({
        url: `/invoices/match-credit-invoice/${invoiceId}`,
        method: 'POST',
        body: {
          creditInvoiceNo,
          amount,
          ...(paidDate ? { paidDate } : {}),
          ...(additionalInformation ? { additionalInformation } : {}),
        },
      }),
      async onQueryStarted({ invoiceId, creditInvoiceNo, amount }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          await delay(10000);

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));

          return;
        } catch {
          // Handle errors
        }
      },
    }),

    resendInvoice: builder.mutation<string, { invoiceId: string; distributionMethod: string }>({
      query: ({ invoiceId, distributionMethod }) => ({
        url: `/invoices/${invoiceId}/resend`,
        method: 'POST',
        body: {
          distributionMethod,
        },
      }),

      async onQueryStarted({ invoiceId, distributionMethod }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          await delay(10000);

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));

          return;
        } catch {
          console.log('Error resending invoice!');
        }
      },
    }),

    getInvoiceModifyEligibility: builder.query<IInvoiceModifyEligibility, { invoiceId: string }>({
      query: ({ invoiceId }) => `/invoices/${invoiceId}/modify-eligibility`,
    }),

    previewResetInvoiceStage: builder.mutation<
      IInvoiceResetStagePreview,
      { invoiceId: string; body: IInvoiceResetStageRequest }
    >({
      query: ({ invoiceId, body }) => ({
        url: `/invoices/${invoiceId}/reset-stage/preview`,
        method: 'POST',
        body,
      }),
    }),

    resetInvoiceStage: builder.mutation<
      IInvoiceResetStagePreview,
      { invoiceId: string; body: IInvoiceResetStageRequest }
    >({
      query: ({ invoiceId, body }) => ({
        url: `/invoices/${invoiceId}/reset-stage`,
        method: 'POST',
        body,
      }),

      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));
        } catch {
          console.log('Error resetting invoice stage!');
        }
      },
    }),

    modifyAndResendInvoice: builder.mutation<
      string,
      { invoiceId: string; body: Partial<IModifyAndResend> }
    >({
      query: ({ invoiceId, body }) => ({
        url: `/invoices/${invoiceId}/modify-and-resend`,
        method: 'POST',
        body,
      }),

      async onQueryStarted({ invoiceId, body }, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          await delay(10000);

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));

          return;
        } catch {
          console.log('Error on modify and resending invoice!');
        }
      },
    }),

    getPreviewId: builder.mutation<
      { previewId: string },
      {
        invoiceId: string;
        body: Partial<IModifyAndResend> & { previewType: InvoiceDocumentPreviewType };
      }
    >({
      query: ({ invoiceId, body }) => ({
        url: `/invoices/${invoiceId}/invoice-document-preview`,
        method: 'POST',
        body,
      }),
    }),

    getDocumentsPreview: builder.mutation<
      {
        status: string;
        data: {
          pdfUrl?: string | null;
          messageBody?: string | null;
          status: string;
        };
      },
      { invoiceId: string; previewId: string }
    >({
      query: ({ invoiceId, previewId }) => ({
        url: `/invoices/${invoiceId}/invoice-document-preview/${previewId}`,
        method: 'GET',
      }),
    }),

    getItemCalculations: builder.query<DataResponse<any>, any>({
      query: (requestBody) => ({
        url: `/invoices/calculate-item`,
        method: 'POST',
        body: requestBody,
      }),
      // transformResponse: (res: DataResponse<any>) => {
      //   res.data = {
      //     name: "SOME NAME",
      //     unitPrice: -15,
      //     quantity: 5,
      //     calcData: {
      //       VATPercent: 25,
      //       totalAmount: 555555,
      //       VATAmount: 500,
      //     }
      //   }

      //   return res;
      // },
    }),

    // Retry attempts
    getRetryDirectDebitHistoryAttempts: builder.query<
      DirectDebitRetryAttempts,
      {
        invoiceId: string;
        page: number;
        limit: number;
      }
    >({
      query: ({ invoiceId, page, limit }) =>
        `/invoices/${invoiceId}/retry-attempts?page=${page}&limit=${limit}`,
    }),

    getTotalBreakdown: builder.query<
      DataResponse<{
        totalCapital: number;
        totalFees: number;
        currency: string;
        feeBreakdown: {
          date: string;
          total: number;
          items: {
            date: string;
            description: string;
            holder: string;
            amount: number;
          }[];
        }[];
      }>,
      string
    >({
      query: (invoiceId) => `/invoices/${invoiceId}/total-breakdown`,
    }),

    getWorkflowsSettings: builder.query<{ [key: string]: any }, { id: string }>({
      query: ({ id }) => `/invoices/${id}/workflows-settings`,
    }),

    removeReminderFee: builder.query<
      ISingleInvoiceResponse,
      { id: string; customerId: string; comment: string }
    >({
      query: ({ id, comment }) => ({
        url: `/invoices/remove-reminder-fee/${id}`,
        method: 'POST',
        body: { comment },
      }),
      async onQueryStarted({ id, customerId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          await delay(5000);

          dispatch(invoicesAPISlice.util.invalidateTags(['Invoices', 'Customers']));
        } catch {
          console.log('Error removing reminder fee!');
        }
      },
    }),

    getInvoicePauses: builder.query<IInvoicePause[], { invoiceId: string }>({
      query: ({ invoiceId }) => `/invoices/${invoiceId}/pauses`,
      providesTags: ['InvoicePauses'],
    }),

    createInvoicePause: builder.mutation<
      IInvoicePause,
      { invoiceId: string; requestData: ICreateInvoicePause }
    >({
      query: ({ invoiceId, requestData }) => ({
        url: `/invoices/${invoiceId}/pauses`,
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['InvoicePauses', 'Invoices', 'EventLogs'],
    }),

    updateInvoicePause: builder.mutation<
      IInvoicePause,
      { invoiceId: string; pauseId: string; requestData: IUpdateInvoicePause }
    >({
      query: ({ invoiceId, pauseId, requestData }) => ({
        url: `/invoices/${invoiceId}/pauses/${pauseId}`,
        method: 'PATCH',
        body: requestData,
      }),
      invalidatesTags: ['InvoicePauses', 'Invoices', 'EventLogs'],
    }),

    cancelInvoicePause: builder.mutation<
      IInvoicePause,
      { invoiceId: string; pauseId: string; requestData: ICancelInvoicePause }
    >({
      query: ({ invoiceId, pauseId, requestData }) => ({
        url: `/invoices/${invoiceId}/pauses/${pauseId}/cancel`,
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['InvoicePauses', 'Invoices', 'EventLogs'],
    }),
  }),
});

export const {
  useLazySearchInvoicesQuery,
  useGetInvoiceByIdQuery,
  useLazyCheckInvoiceStageQuery,
  useGetInvoicesQuery,
  useGetInvoicesPerCustomerQuery,
  useGetInvoicesPerClientQuery,
  useLazyGetInvoicesQuery,
  useGetInvoiceStatisticsQuery,
  useGetEventLogsQuery,
  useLazySetCretidLossQuery,
  useLazyPostponeInvoiceQuery,
  useLazyAdjustDueDateQuery,
  useLazyGoodWillWaiveQuery,
  useLazyDirectPaymentQuery,
  useLazyCancelInvoicesQuery,
  useLazyInvoiceStopUntilQuery,
  useLazyCreditInvoiceQuery,
  useGetInvoicesByClientIdQuery,
  useLazyNewCreditInvoiceQuery,
  useLazyMatchCreditInvoiceQuery,
  useResendInvoiceMutation,
  useGetPreviewIdMutation,
  useGetDocumentsPreviewMutation,
  useLazyGetInvoiceModifyEligibilityQuery,
  useModifyAndResendInvoiceMutation,
  usePreviewResetInvoiceStageMutation,
  useResetInvoiceStageMutation,
  useLazyGetItemCalculationsQuery,
  useLazyGetRetryDirectDebitHistoryAttemptsQuery,
  useGetTotalBreakdownQuery,
  useExportInvoicesMutation,
  useGetWorkflowsSettingsQuery,
  useLazyRemoveReminderFeeQuery,
  useGetInvoicePausesQuery,
  useCreateInvoicePauseMutation,
  useUpdateInvoicePauseMutation,
  useCancelInvoicePauseMutation,
} = invoicesAPISlice;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const parseBaseInvoice = (x: any): IBaseInvoice => {
  return {
    id: x.id,
    invoiceNo: x.invoiceNo,
    dueDate: x.dueDate,
    invoiceDate: x.invoiceDate,
    countryCode: x.countryCode,
    paymentReference: x.paymentReference,
    bankAccount: x.bankAccount,
    status: x.status,
    stage: x.stage,
    paymentStatus: x.paymentStatus,
    currency: x.currency,
    customerId: x.customerId,
    customerNo: x.customerNo,
    customer: {
      id: x.customerId,
      customerNo: x.customerNo,
      customerName: x.customer?.customerName || '',
    },
    client: {
      id: x.clientId,
      name: x.clientName,
      logo_uri: x.clientLogo,
    },
    metadata: x.metadata,
    totalRemainingBalance: parseFloat(x.totalRemainingBalance),
    totalAmount: parseFloat(x.totalAmount),
    remainingAmount: parseFloat(x.remainingAmount),
    score: parseFloat(x.rank_score),
    lastPaymentTransactionType: x.lastPaymentTransactionType,
  };
};

// DO NOT DELETE. CAN BE USED FOR FUTER DEBUGING/TESTING
// queryFn: async (
//   arg,
//   api,
//   extraOptions,
//   baseQuery
// ): Promise<QueryReturnValue<never, FetchBaseQueryError, FetchBaseQueryMeta>> => {
//   await delay(3000);

//   // simulate a thrown network-like error
//   // throw {
//   //   status: 500,
//   //   data: { message: 'Simulated thrown error' },
//   // } as FetchBaseQueryError;

//   // simulate error using rejectWithValue-like shape
//   return {
//     error: {
//       status: 400,
//       data: { message: 'Simulated error with payload' },
//     } as FetchBaseQueryError,
//   };
// },
